import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  CalendarIcon, 
  Download, 
  TrendingUp, 
  TrendingDown,
  Target,
  Settings2,
  ArrowUp,
  ArrowDown,
  Minus,
  AlertTriangle,
  ChefHat,
  Info
} from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';

const PLReportPage = () => {
  const { currentLocation, api } = useAuth();
  const [pl, setPL] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [targets, setTargets] = useState(null);
  const [cogsDetails, setCogsDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({
    food_cost_pct: '',
    labor_pct: '',
    net_profit_pct: ''
  });

  useEffect(() => {
    if (currentLocation) {
      fetchPLData();
    }
  }, [currentLocation, startDate, endDate]);

  const fetchPLData = async () => {
    setLoading(true);
    try {
      const month = format(startDate, 'yyyy-MM');
      const [plRes, expRes, targetsRes, cogsRes] = await Promise.all([
        api.get(`/pl/${currentLocation.id}/with-targets`, {
          params: {
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          }
        }),
        api.get('/expenses', {
          params: {
            location_id: currentLocation.id,
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          }
        }),
        api.get(`/pl-targets/${currentLocation.id}`, {
          params: { month }
        }).catch(() => ({ data: null })),
        api.get(`/pl/${currentLocation.id}/cogs-details`, {
          params: {
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
          }
        }).catch(() => ({ data: null }))
      ]);
      setPL(plRes.data);
      setExpenses(expRes.data.filter(e => e.confirmed));
      setTargets(targetsRes.data);
      setCogsDetails(cogsRes.data);
      
      // Pre-fill target form if targets exist
      if (targetsRes.data) {
        setTargetForm({
          food_cost_pct: targetsRes.data.food_cost_pct?.toString() || '',
          labor_pct: targetsRes.data.labor_pct?.toString() || '',
          net_profit_pct: targetsRes.data.net_profit_pct?.toString() || ''
        });
      }
    } catch (err) {
      console.error('Error fetching P&L:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTargets = async () => {
    try {
      const data = {
        location_id: currentLocation.id,
        month: format(startDate, 'yyyy-MM'),
        food_cost_pct: targetForm.food_cost_pct ? parseFloat(targetForm.food_cost_pct) : null,
        labor_pct: targetForm.labor_pct ? parseFloat(targetForm.labor_pct) : null,
        net_profit_pct: targetForm.net_profit_pct ? parseFloat(targetForm.net_profit_pct) : null
      };
      
      await api.put('/pl-targets', data);
      toast.success('Targets saved');
      setTargetDialogOpen(false);
      fetchPLData(); // Refresh to show new targets
    } catch (err) {
      console.error('Error saving targets:', err);
      toast.error('Failed to save targets');
    }
  };

  const formatCurrency = (val) => `£${val?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}`;

  // Calculate percentages
  const foodCostPct = pl?.total_revenue ? (pl.cost_of_goods_sold / pl.total_revenue * 100) : 0;
  const laborPct = pl?.total_revenue ? (pl.labor_costs / pl.total_revenue * 100) : 0;
  
  // Variance indicators
  const getVarianceIndicator = (actual, target, invertColors = false) => {
    if (target === null || target === undefined) return null;
    const variance = actual - target;
    const isGood = invertColors ? variance > 0 : variance < 0; // For costs, lower is better; for profit, higher is better
    
    if (Math.abs(variance) < 0.5) {
      return (
        <Badge variant="outline" className="ml-2 text-xs font-normal">
          <Minus className="w-3 h-3 mr-1" />
          On target
        </Badge>
      );
    }
    
    return (
      <Badge 
        variant={isGood ? "default" : "destructive"} 
        className="ml-2 text-xs font-normal"
      >
        {variance > 0 ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
        {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
      </Badge>
    );
  };

  // Group expenses by category
  const expensesByCategory = {
    cogs: expenses.filter(e => e.category === 'cogs'),
    labour: expenses.filter(e => e.category === 'labor'),
    operating: expenses.filter(e => e.category === 'operating')
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="pl-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profit & Loss Report</h1>
          <p className="text-muted-foreground">{currentLocation?.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Targets Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setTargetDialogOpen(true)}
            data-testid="set-targets-btn"
          >
            <Target className="w-4 h-4 mr-2" />
            Set Targets
          </Button>
          {/* Start Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(startDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => date && setStartDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <span className="text-muted-foreground">to</span>
          {/* End Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(endDate, 'dd MMM yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => date && setEndDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Targets Summary (if set) */}
      {targets && (targets.food_cost_pct || targets.labor_pct || targets.net_profit_pct) && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-medium">Monthly Targets ({format(startDate, 'MMMM yyyy')})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-6">
              {targets.food_cost_pct !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Food Cost:</span>
                  <span className="font-semibold">{targets.food_cost_pct}%</span>
                  {getVarianceIndicator(foodCostPct, targets.food_cost_pct, false)}
                </div>
              )}
              {targets.labor_pct !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Labor:</span>
                  <span className="font-semibold">{targets.labor_pct}%</span>
                  {getVarianceIndicator(laborPct, targets.labor_pct, false)}
                </div>
              )}
              {targets.net_profit_pct !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Net Profit:</span>
                  <span className="font-semibold">{targets.net_profit_pct}%</span>
                  {getVarianceIndicator(pl?.net_margin_pct || 0, targets.net_profit_pct, true)}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* P&L Statement */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Profit & Loss Statement</CardTitle>
              <CardDescription>
                {format(startDate, 'MMMM d, yyyy')} — {format(endDate, 'MMMM d, yyyy')}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" disabled>
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md" data-testid="pl-statement">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="text-right font-semibold">Amount</TableHead>
                  <TableHead className="text-right font-semibold">% of Revenue</TableHead>
                  {targets && <TableHead className="text-right font-semibold">Target</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Revenue Section */}
                <TableRow className="bg-profit/5">
                  <TableCell className="font-semibold text-profit">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Total Revenue
                    </div>
                  </TableCell>
                  <TableCell className="text-right mono-num font-semibold text-profit" data-testid="pl-revenue">
                    {formatCurrency(pl?.total_revenue)}
                  </TableCell>
                  <TableCell className="text-right mono-num text-muted-foreground">
                    100.0%
                  </TableCell>
                  {targets && <TableCell></TableCell>}
                </TableRow>

                {/* COGS Section */}
                <TableRow>
                  <TableCell className="pl-8 text-muted-foreground">Cost of Goods Sold</TableCell>
                  <TableCell className="text-right mono-num text-loss" data-testid="pl-cogs">
                    ({formatCurrency(pl?.cost_of_goods_sold)})
                  </TableCell>
                  <TableCell className="text-right mono-num text-muted-foreground">
                    <span className="flex items-center justify-end">
                      {foodCostPct.toFixed(1)}%
                      {targets?.food_cost_pct !== null && getVarianceIndicator(foodCostPct, targets?.food_cost_pct, false)}
                    </span>
                  </TableCell>
                  {targets && (
                    <TableCell className="text-right mono-num text-muted-foreground">
                      {targets.food_cost_pct !== null ? `${targets.food_cost_pct}%` : '-'}
                    </TableCell>
                  )}
                </TableRow>

                {/* Gross Profit */}
                <TableRow className="border-t-2">
                  <TableCell className="font-semibold">Gross Profit</TableCell>
                  <TableCell className="text-right mono-num font-semibold" data-testid="pl-gross-profit">
                    {formatCurrency(pl?.gross_profit)}
                  </TableCell>
                  <TableCell className="text-right mono-num font-medium">
                    {pl?.gross_margin_pct?.toFixed(1) || 0}%
                  </TableCell>
                  {targets && <TableCell></TableCell>}
                </TableRow>

                {/* Operating Expenses Header */}
                <TableRow className="bg-muted/30">
                  <TableCell colSpan={targets ? 4 : 3} className="font-semibold text-muted-foreground">
                    Operating Expenses
                  </TableCell>
                </TableRow>

                {/* Labour */}
                <TableRow>
                  <TableCell className="pl-8 text-muted-foreground">Labour Costs</TableCell>
                  <TableCell className="text-right mono-num text-loss" data-testid="pl-labour">
                    ({formatCurrency(pl?.labor_costs)})
                  </TableCell>
                  <TableCell className="text-right mono-num text-muted-foreground">
                    <span className="flex items-center justify-end">
                      {laborPct.toFixed(1)}%
                      {targets?.labor_pct !== null && getVarianceIndicator(laborPct, targets?.labor_pct, false)}
                    </span>
                  </TableCell>
                  {targets && (
                    <TableCell className="text-right mono-num text-muted-foreground">
                      {targets.labor_pct !== null ? `${targets.labor_pct}%` : '-'}
                    </TableCell>
                  )}
                </TableRow>

                {/* Operating */}
                <TableRow>
                  <TableCell className="pl-8 text-muted-foreground">Operating Expenses</TableCell>
                  <TableCell className="text-right mono-num text-loss" data-testid="pl-operating">
                    ({formatCurrency(pl?.operating_expenses)})
                  </TableCell>
                  <TableCell className="text-right mono-num text-muted-foreground">
                    {pl?.total_revenue ? ((pl?.operating_expenses / pl?.total_revenue) * 100).toFixed(1) : 0}%
                  </TableCell>
                  {targets && <TableCell></TableCell>}
                </TableRow>

                {/* Total Expenses */}
                <TableRow className="border-t">
                  <TableCell className="pl-8 font-medium">Total Operating Expenses</TableCell>
                  <TableCell className="text-right mono-num font-medium text-loss">
                    ({formatCurrency((pl?.labor_costs || 0) + (pl?.operating_expenses || 0))})
                  </TableCell>
                  <TableCell className="text-right mono-num text-muted-foreground">
                    {pl?.total_revenue ? (((pl?.labor_costs + pl?.operating_expenses) / pl?.total_revenue) * 100).toFixed(1) : 0}%
                  </TableCell>
                  {targets && <TableCell></TableCell>}
                </TableRow>

                {/* Net Profit */}
                <TableRow className={`border-t-2 ${pl?.net_profit >= 0 ? 'bg-profit/5' : 'bg-loss/5'}`}>
                  <TableCell className="font-bold text-lg">
                    <div className="flex items-center gap-2">
                      {pl?.net_profit >= 0 ? (
                        <TrendingUp className="w-5 h-5 text-profit" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-loss" />
                      )}
                      Net Profit
                    </div>
                  </TableCell>
                  <TableCell 
                    className={`text-right mono-num font-bold text-lg ${pl?.net_profit >= 0 ? 'text-profit' : 'text-loss'}`}
                    data-testid="pl-net-profit"
                  >
                    {formatCurrency(pl?.net_profit)}
                  </TableCell>
                  <TableCell className={`text-right mono-num font-bold ${pl?.net_profit >= 0 ? 'text-profit' : 'text-loss'}`}>
                    <span className="flex items-center justify-end">
                      {pl?.net_margin_pct?.toFixed(1) || 0}%
                      {targets?.net_profit_pct !== null && getVarianceIndicator(pl?.net_margin_pct || 0, targets?.net_profit_pct, true)}
                    </span>
                  </TableCell>
                  {targets && (
                    <TableCell className="text-right mono-num font-medium">
                      {targets.net_profit_pct !== null ? `${targets.net_profit_pct}%` : '-'}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Expense Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* COGS - Enhanced with blended % approach */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Cost of Goods Sold
              {cogsDetails?.has_calculated_cogs && (
                <Badge 
                  variant={cogsDetails?.summary?.confidence === 'high' ? 'default' : cogsDetails?.summary?.confidence === 'medium' ? 'secondary' : 'outline'}
                  className="text-xs font-normal"
                >
                  {cogsDetails?.summary?.confidence === 'high' ? '✓ High' : cogsDetails?.summary?.confidence === 'medium' ? '~Medium' : '? Low'} confidence
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {cogsDetails?.has_calculated_cogs 
                ? `Blended ${cogsDetails?.summary?.blended_food_cost_pct || 30}% food cost (${cogsDetails?.summary?.recipe_coverage_pct || 0}% recipe coverage)`
                : 'Based on food purchases (upload Product Mix for blended COGS)'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num text-loss mb-4">
              {formatCurrency(pl?.cost_of_goods_sold)}
            </div>
            
            {cogsDetails?.has_calculated_cogs ? (
              <div className="space-y-3">
                {/* Recipe coverage progress */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Recipe Coverage (by revenue)</span>
                    <span className="font-medium">{cogsDetails?.summary?.recipe_coverage_pct || 0}%</span>
                  </div>
                  <Progress value={cogsDetails?.summary?.recipe_coverage_pct || 0} className="h-2" />
                </div>
                
                {/* Blended % breakdown */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Blended Food Cost %</span>
                    <span className="mono-num font-semibold">{cogsDetails?.summary?.blended_food_cost_pct || 30}%</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">From Recipes (avg {cogsDetails?.summary?.recipe_avg_food_cost_pct || 0}%)</span>
                    <span className="mono-num text-profit">{formatCurrency(cogsDetails?.summary?.cogs_from_recipes || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Estimated ({cogsDetails?.summary?.default_food_cost_pct || 30}%)</span>
                    <span className="mono-num text-amber-600">{formatCurrency(cogsDetails?.summary?.cogs_estimated || 0)}</span>
                  </div>
                  {(cogsDetails?.summary?.modifier_cogs !== 0 || cogsDetails?.summary?.modifier_revenue > 0) && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">
                        Modifiers ({cogsDetails?.summary?.modifier_cogs < 0 ? 'savings' : 'additions'})
                      </span>
                      <span className={`mono-num ${cogsDetails?.summary?.modifier_cogs < 0 ? 'text-green-600' : 'text-amber-600'}`}>
                        {cogsDetails?.summary?.modifier_cogs < 0 ? '-' : '+'}{formatCurrency(Math.abs(cogsDetails?.summary?.modifier_cogs || 0))}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Confidence explanation */}
                <div className="pt-2 border-t text-xs text-muted-foreground">
                  {cogsDetails?.summary?.confidence === 'high' 
                    ? '✓ High confidence: >70% of revenue has recipe-based costs'
                    : cogsDetails?.summary?.confidence === 'medium'
                    ? '~Medium confidence: 30-70% of revenue has recipe-based costs'
                    : '? Low confidence: <30% of revenue has recipe-based costs. Add more recipes to improve accuracy.'
                  }
                </div>
                
                {/* Items needing recipes */}
                {cogsDetails?.items_needing_recipes?.length > 0 && cogsDetails?.summary?.confidence !== 'high' && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center gap-1 text-xs text-amber-600 mb-2">
                      <AlertTriangle className="w-3 h-3" />
                      Top items to add recipes (by revenue):
                    </div>
                    <div className="space-y-1">
                      {cogsDetails.items_needing_recipes.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="truncate flex-1 text-muted-foreground">{item.item_name}</span>
                          <span className="mono-num ml-2">{formatCurrency(item.revenue)}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="text-xs p-0 h-auto mt-1"
                      onClick={() => window.location.href = '/recipes'}
                    >
                      <ChefHat className="w-3 h-3 mr-1" />
                      Create recipes →
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Legacy: Show food purchases */}
                <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <Info className="w-4 h-4 flex-shrink-0" />
                  <span>COGS is based on food purchases. Upload Product Mix data for blended COGS calculation.</span>
                </div>
                
                {expensesByCategory.cogs.length > 0 ? (
                  <div className="space-y-2 text-sm">
                    {expensesByCategory.cogs.slice(0, 5).map((exp) => (
                      <div key={exp.id} className="flex justify-between">
                        <span className="truncate flex-1 text-muted-foreground">{exp.vendor}</span>
                        <span className="mono-num ml-2">{formatCurrency(exp.amount)}</span>
                      </div>
                    ))}
                    {expensesByCategory.cogs.length > 5 && (
                      <p className="text-xs text-muted-foreground">+{expensesByCategory.cogs.length - 5} more</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No food purchase entries</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Labour */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Labour Costs</CardTitle>
            <CardDescription>{expensesByCategory.labour.length} entries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num text-loss mb-4">
              {formatCurrency(pl?.labor_costs)}
            </div>
            {expensesByCategory.labour.length > 0 ? (
              <div className="space-y-2 text-sm">
                {expensesByCategory.labour.slice(0, 5).map((exp) => (
                  <div key={exp.id} className="flex justify-between">
                    <span className="truncate flex-1 text-muted-foreground">{exp.vendor}</span>
                    <span className="mono-num ml-2">{formatCurrency(exp.amount)}</span>
                  </div>
                ))}
                {expensesByCategory.labour.length > 5 && (
                  <p className="text-xs text-muted-foreground">+{expensesByCategory.labour.length - 5} more</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No entries</p>
            )}
          </CardContent>
        </Card>

        {/* Operating */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Operating Expenses</CardTitle>
            <CardDescription>{expensesByCategory.operating.length} entries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num text-loss mb-4">
              {formatCurrency(pl?.operating_expenses)}
            </div>
            {expensesByCategory.operating.length > 0 ? (
              <div className="space-y-2 text-sm">
                {expensesByCategory.operating.slice(0, 5).map((exp) => (
                  <div key={exp.id} className="flex justify-between">
                    <span className="truncate flex-1 text-muted-foreground">{exp.vendor}</span>
                    <span className="mono-num ml-2">{formatCurrency(exp.amount)}</span>
                  </div>
                ))}
                {expensesByCategory.operating.length > 5 && (
                  <p className="text-xs text-muted-foreground">+{expensesByCategory.operating.length - 5} more</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No entries</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Set Targets Dialog */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Set P&L Targets
            </DialogTitle>
            <DialogDescription>
              Set monthly percentage targets for {format(startDate, 'MMMM yyyy')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="food_cost">Food Cost Target (%)</Label>
              <Input
                id="food_cost"
                type="number"
                step="0.1"
                min="0"
                max="100"
                placeholder="e.g., 28"
                value={targetForm.food_cost_pct}
                onChange={(e) => setTargetForm({...targetForm, food_cost_pct: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Industry standard: 25-35% for cafés
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="labor">Labor Cost Target (%)</Label>
              <Input
                id="labor"
                type="number"
                step="0.1"
                min="0"
                max="100"
                placeholder="e.g., 30"
                value={targetForm.labor_pct}
                onChange={(e) => setTargetForm({...targetForm, labor_pct: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Industry standard: 25-35% for full-service
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="net_profit">Net Profit Target (%)</Label>
              <Input
                id="net_profit"
                type="number"
                step="0.1"
                min="-100"
                max="100"
                placeholder="e.g., 15"
                value={targetForm.net_profit_pct}
                onChange={(e) => setTargetForm({...targetForm, net_profit_pct: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                Industry standard: 5-15% for restaurants
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTargetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTargets} data-testid="save-targets-btn">
              Save Targets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PLReportPage;
